/**
 * 一次列表请求处在哪个阶段。
 *
 * 只用布尔值 `loading` 区分不出「还在路上」和「确实一条都没有」——两种情况下
 * 数组都是空的，渲染层会把它们一起当成空态。网络慢的时候客户先看到一句
 * 「还没有商品 / 还没有图片」，过几秒内容又冒出来，第一反应是数据丢了、
 * 或者自己刚删错了什么。所以加载中、失败、真的为空必须是三个不同的状态。
 *
 * 商品目录、媒体库、资料库、询盘、页面内容共用这一份：各页的空态文案不同，
 * 但"什么时候能写空态文案"是同一条判断，分散写两份迟早会漂。
 *
 * ## 为什么只有三态，"空"要另外派生
 *
 * 「空」不是并行的第四态，而是**成立在 `ready` 之后的一个结论**：
 *
 *     const phase = loadPhaseOf({ loading, error });
 *     const isEmpty = phase === 'ready' && items.length === 0;
 *
 * 这样"还在读"和"读失败"在类型上就不可能被说成"你没有内容"，
 * 不需要每个人每次都想一遍先后顺序。反过来把 `empty` 也塞进枚举，
 * 就变成了四个状态要自己排优先级 —— 谁先判谁就赢，那正是当初出错的地方。
 */
export type LoadPhase = 'loading' | 'ready' | 'error';

/**
 * 判断顺序：**加载中 > 出错 > 完成**。
 *
 * 两个条件同时成立时（比如刷新失败、手上还留着上一批数据），
 * 优先说"没读全"，而不是"读完了" —— 让客户把一份不完整的数据当成全部，
 * 比多显示一条错误严重。
 */
export function loadPhaseOf(input: { loading: boolean; error: string | null }): LoadPhase {
  if (input.loading) return 'loading';
  if (input.error) return 'error';
  return 'ready';
}
